import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Reusable Image Upload Component
 * @param {string} value - Current image URL/path
 * @param {function} onChange - Callback when image changes (receives URL)
 * @param {string} label - Label text
 * @param {string} placeholder - Placeholder text
 * @param {string} aspectRatio - CSS aspect ratio (e.g., "1/1", "16/9", "4/3")
 */
const ImageUploader = ({ 
  value, 
  onChange, 
  label = "Görsel", 
  placeholder = "Görsel yükleyin",
  aspectRatio = "1/1"
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const getToken = () => localStorage.getItem('admin_token');

  const getImageUrl = (url) => {
    if (!url) return null;
    // If it's already a full URL or starts with /api, use as is
    if (url.startsWith('http') || url.startsWith('/api')) {
      return url.startsWith('/api') ? `${API_BASE_URL}${url}` : url;
    }
    // Otherwise assume it's a filename from uploads
    return `${API_BASE_URL}/api/uploads/image/${url}`;
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Geçersiz dosya formatı. JPEG, PNG, WebP, GIF veya SVG yükleyin.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu çok büyük. Maksimum 5MB.');
      return;
    }

    setUploading(true);
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_BASE_URL}/api/uploads/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        onChange(data.url);
        toast.success('Görsel yüklendi');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Yükleme başarısız');
      }
    } catch (error) {
      toast.error('Yükleme sırasında hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleRemove = () => {
    onChange('');
  };

  const displayUrl = getImageUrl(value);

  return (
    <div>
      <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>{label}</label>
      
      <div
        className={`relative rounded-lg overflow-hidden transition-all ${dragOver ? 'ring-2 ring-cyan-400' : ''}`}
        style={{
          background: 'rgba(15, 23, 42, 0.8)',
          border: `2px dashed ${dragOver ? '#00F0FF' : 'rgba(0, 240, 255, 0.3)'}`,
          aspectRatio: aspectRatio
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {displayUrl ? (
          // Preview
          <div className="relative w-full h-full group">
            <img
              src={displayUrl}
              alt="Preview"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-2 rounded-lg transition-colors"
                style={{ background: 'rgba(0, 240, 255, 0.3)', color: '#00F0FF' }}
                title="Değiştir"
              >
                <Upload size={20} />
              </button>
              <button
                type="button"
                onClick={handleRemove}
                className="p-2 rounded-lg transition-colors"
                style={{ background: 'rgba(239, 68, 68, 0.3)', color: '#EF4444' }}
                title="Kaldır"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        ) : (
          // Upload placeholder
          <div 
            className="w-full h-full flex flex-col items-center justify-center cursor-pointer p-4"
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <Loader2 className="animate-spin mb-2" size={32} style={{ color: '#00F0FF' }} />
            ) : (
              <ImageIcon size={32} className="mb-2" style={{ color: '#64748B' }} />
            )}
            <p className="text-sm text-center" style={{ color: '#94A3B8' }}>
              {uploading ? 'Yükleniyor...' : placeholder}
            </p>
            <p className="text-xs mt-1" style={{ color: '#64748B' }}>
              Sürükle & Bırak veya tıkla
            </p>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
          onChange={handleInputChange}
          className="hidden"
        />
      </div>

      {/* File info */}
      {value && (
        <p className="text-xs mt-2 truncate" style={{ color: '#64748B' }}>
          {value.split('/').pop()}
        </p>
      )}
    </div>
  );
};

export default ImageUploader;
